<script setup lang="ts">
import { computed, watch, reactive, ref } from "vue";
import { McInputDate } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { FilterType, useApplyFilter, FilterDefinition, FilterTypeEnum } from "@/features/filters";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  filter: FilterDefinition;
  zonePrefix: string;
  filterType: FilterType;
}>();

const { t } = useI18n();

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const applyStoreFilter = (zonePrefix: string, filterKey: string, payload: any) => {
  if (props.filterType === "panel-filters" && filtersStore.applyPanelSelectedFilters) {
    filtersStore.applyPanelSelectedFilters(zonePrefix, filterKey, payload);
  } else if (props.filterType !== "panel-filters" && filtersStore.applyFilters) {
    filtersStore.applyFilters(zonePrefix, filterKey, payload);
  }
};

const hasUserCleared = ref(false);

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = source?.[props.zonePrefix]?.[props.filter.key];
  return {
    ...(storeValue || { label: "", value: "" }),
    filterType: props.filterType
  };
});

const internalDateRange = reactive({
  startDate: "",
  endDate: ""
});

watch(
  internalDateRange,
  (newVal) => {
    let { startDate, endDate } = newVal;

    if (props.filter.includeTime && startDate && endDate) {
      startDate = `${startDate}T00:00:00`;
      endDate = `${endDate}T23:59:59`;
    }

    const groupId = props.filter.groupId;
    const groupLabel = props.filter.groupLabel;
    const sectionLabel = props.filter.sectionLabel;

    const payload = {
      value: { startDate, endDate },
      filterKey: props.filter.key,
      filterType: props.filterType,
      groupId,
      groupLabel,
      sectionLabel
    };

    applyStoreFilter(props.zonePrefix, props.filter.key, payload);
  },
  { deep: true }
);

const fromLabelText = computed(() => (props.filter.hiddenlabel ? "" : props.filter.fromLabel || "FROM"));
const toLabelText = computed(() => (props.filter.hiddenlabel ? "" : props.filter.toLabel || "TO"));
const fromPlaceholderText = computed(() => props.filter.fromPlaceholder || "YYYY-MM-DD");
const toPlaceholderText = computed(() => props.filter.toPlaceholder || "YYYY-MM-DD");
const containerFlexDirection = computed(() => props.filter.layoutDirection || "column");
const computedInputWidth = computed(() => props.filter.width || "100%");

const minMaxDate = computed(() => {
  const start = internalDateRange.startDate;
  const end = internalDateRange.endDate;

  return {
    startDateMin: props.filter.min || null,
    startDateMax: end || props.filter.max || null,
    endDateMin: start || props.filter.min || null,
    endDateMax: props.filter.max || null
  };
});

const onClear = (field: "startDate" | "endDate") => {
  internalDateRange[field] = "";
  if (!internalDateRange.startDate && !internalDateRange.endDate) {
    removeFilter(props.filter.key);
  }
};

watch(
  selectedFilter,
  (val) => {
    if (!hasUserCleared.value) {
      internalDateRange.startDate = val?.value?.startDate?.includes("T")
        ? val.value.startDate.split("T")[0]
        : val.value.startDate;
      internalDateRange.endDate = val?.value?.endDate?.includes("T")
        ? val.value.endDate.split("T")[0]
        : val.value.endDate;
    }
    if (!val?.value) {
      hasUserCleared.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="date-range-filter" :style="`flex-direction: ${containerFlexDirection}`">
    <mc-input-date
      v-model="internalDateRange.startDate"
      class="date-range-filter--input"
      fit="small"
      :yearcap="20"
      :label="t(fromLabelText)"
      :placeholder="t(fromPlaceholderText)"
      name="from-date"
      :min="minMaxDate.startDateMin"
      :max="minMaxDate.startDateMax"
      :clearbutton="filter.clearButton"
      @clearbuttonclick.prevent="() => onClear('startDate')"
    />
    <mc-input-date
      v-model="internalDateRange.endDate"
      class="date-range-filter--input"
      fit="small"
      :yearcap="20"
      :label="t(toLabelText)"
      :placeholder="t(toPlaceholderText)"
      name="to-date"
      :min="minMaxDate.endDateMin"
      :max="minMaxDate.endDateMax"
      :style="{ width: computedInputWidth }"
      :clearbutton="filter.clearButton"
      @clearbuttonclick.prevent="() => onClear('endDate')"
    />
  </div>
</template>

<style scoped lang="scss">
.date-range-filter {
  display: flex;
  gap: 4px;
}
</style>
